import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * One navigation stack for the whole app, on top of history.pushState.
 *
 * There was none. The tab was a useState, the chat was a conditional render,
 * and the document history stayed empty — which has consequences on both
 * phones and neither of them is subtle:
 *
 * - Android: MainActivity's onBackPressed asks webView.canGoBack(), gets
 *   false because nothing was ever pushed, and closes the app. Hardware Back
 *   from inside a conversation quits Voxiox.
 * - iOS: allowsBackForwardNavigationGestures is on, and the edge swipe walks
 *   a history the SPA never wrote.
 *
 * Pushing real entries fixes the Android case without touching Kotlin —
 * canGoBack() starts returning true — and gives every screen an address, so a
 * banner can send somebody straight to the setting it is about.
 *
 * Deliberately small: a stack of names, not a router. The app has one window
 * and a handful of overlays; a router would be a second source of truth about
 * what is on screen.
 */
export function useNavStack() {
  const [stack, setStack] = useState([]);
  // The stack the popstate listener reads. State alone would be stale inside
  // an event handler registered once.
  const stackRef = useRef([]);
  stackRef.current = stack;
  /** Entries this hook pushed, so a back that leaves the app is recognisable. */
  const ownedRef = useRef(0);

  useEffect(() => {
    const onPop = () => {
      // The browser has already moved; the stack follows it rather than
      // pushing anything back, or Back would fight the person.
      if (ownedRef.current > 0) ownedRef.current -= 1;
      setStack(prev => prev.slice(0, -1));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const push = useCallback((name, data = null) => {
    ownedRef.current += 1;
    window.history.pushState({ vox: name }, '', window.location.pathname + '#' + name);
    setStack(prev => [...prev, { name, data }]);
  }, []);

  /** Go back one screen. Same thing hardware Back does. */
  const pop = useCallback(() => {
    if (stackRef.current.length === 0) return false;
    window.history.back();
    return true;
  }, []);

  /** Leave every pushed screen at once — used when a call takes over. */
  const reset = useCallback(() => {
    const depth = ownedRef.current;
    if (depth > 0) {
      ownedRef.current = 0;
      setStack([]);
      window.history.go(-depth);
    }
  }, []);

  const top = stack.length ? stack[stack.length - 1] : null;
  return { stack, depth: stack.length, top, push, pop, reset };
}
